import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import Flag from '@atlaskit/flag';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';

import { ComponentFlagId, HandleDismissedType } from 'src/redux/flags/types';
import { urls } from 'src/urls';

export const USER_NEEDS_ACCESS_FLAG_ID = 'user-needs-access' as const;

export const messages = defineMessages({
  userNeedsAccessFlagTitleWarning: {
    id: 'frontbucket.flags.userNeedsAccess.titleWarning',
    description: 'Text for title of flag indicating a user needs access',
    defaultMessage: '{displayName} needs access',
  },
  userNeedsAccessFlagDescriptionWarning: {
    id: 'frontbucket.flags.userNeedsAccess.descriptionWarning',
    description:
      'Text for description of flag indicating a user needs access. The "Learn more" link text is wrapped in <link> tags.',
    defaultMessage: `You mentioned {displayName}, but they don’t have the necessary permissions to view or access the content. <link>Learn more</link> about granting access to users and groups.`,
  },
  defaultUser: {
    id: 'frontbucket.flags.userNeedsAccess.defaultUser',
    description: 'Default user',
    defaultMessage: 'User',
  },
});

type Props = {
  id: ComponentFlagId;
  props?: {
    displayName: string;
  };
} & HandleDismissedType;

const UserNeedsAccessWarningFlag = ({
  id,
  props,
  ...otherProps
}: Props): JSX.Element => {
  const intl = useIntl();
  const displayName =
    props?.displayName || intl.formatMessage(messages.defaultUser);
  return (
    <Flag
      {...otherProps}
      id={id}
      title={intl.formatMessage(messages.userNeedsAccessFlagTitleWarning, {
        displayName,
      })}
      description={
        <FormattedMessage
          {...messages.userNeedsAccessFlagDescriptionWarning}
          values={{
            displayName,
            link: chunks => (
              <a
                href={urls.external.grantRepositoryAccessToUsersAndGroups}
                target="_blank"
              >
                {chunks}
              </a>
            ),
          }}
        />
      }
      icon={
        <StatusWarningIcon
          spacing="spacious"
          label="Warning"
          color={token('color.icon.warning')}
        />
      }
    />
  );
};

// eslint-disable-next-line import/no-default-export
export default UserNeedsAccessWarningFlag;
